<template>
    <div class="min-h-screen w-full flex flex-col bg-gradient-to-br from-gray-100 to-orange-50">
        <div class="container mx-auto">
            <div class="px-4 sm:px-8 flex flex-col w-full pb-4 sm:pb-8 mt-4 sm:mt-8">
                <div class="mb-4">
                    <bread-crumb :items="breadCrumbItems"></bread-crumb>
                </div>

                <section class="acc-hero w-full rounded-2xl border shadow-lg p-6 md:p-8 mb-6">
                    <div class="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        <div class="min-w-0 flex-1">
                            <p class="acc-hero__eyebrow">Student Housing · {{ dashboardSemesterLabel }}</p>
                            <h1 class="text-2xl md:text-3xl font-bold text-gray-900 mt-1">
                                {{ campusTitle }} Accommodation
                            </h1>
                            <p class="mt-2 text-gray-600 max-w-2xl leading-relaxed">
                                Villa occupancy, bed capacity, and sponsor distribution — updated each semester by the accommodation custodian.
                            </p>
                        </div>
                        <div class="flex flex-col sm:flex-row gap-2 shrink-0">
                            <button
                                type="button"
                                class="acc-export-btn inline-flex items-center justify-center px-4 py-2.5 rounded-lg text-sm font-semibold"
                                :disabled="pageLoading || isExportExcel"
                                @click="exportAllExcel"
                            >
                                <font-awesome-icon :icon="['fas', 'file-excel']" class="mr-2" />
                                {{ isExportExcel ? "Exporting..." : "Export Excel" }}
                            </button>
                        </div>
                    </div>

                    <div class="acc-semester-bar">
                        <div class="acc-semester-bar__label">
                            <font-awesome-icon :icon="['fas', 'calendar-alt']" class="mr-2" />
                            <span>Semester</span>
                        </div>
                        <div class="acc-semester-bar__pills">
                            <button
                                v-for="opt in semesterFilterOptions"
                                :key="'sem-' + opt.value + opt.label"
                                type="button"
                                class="acc-semester-pill"
                                :class="{ 'acc-semester-pill--active': dashboardSemester === opt.value }"
                                @click="setDashboardSemester(opt.value)"
                            >
                                {{ opt.label }}
                            </button>
                        </div>
                    </div>
                </section>

                <div v-if="pageLoading" class="flex justify-center py-10">
                    <base-page-spinner type="spin" class-list="h-16"></base-page-spinner>
                </div>

                <template v-else>
                    <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4 mb-6">
                        <div class="acc-kpi">
                            <div class="acc-kpi__label">Total capacity</div>
                            <div class="acc-kpi__value">{{ formatNumber(summary.total_capacity) }}</div>
                            <div class="acc-kpi__hint">{{ isDashboardFilterAll ? "Sum across all semesters" : "Configured beds" }}</div>
                        </div>
                        <div class="acc-kpi">
                            <div class="acc-kpi__label">Available beds</div>
                            <div class="acc-kpi__value">{{ formatNumber(summary.available_beds) }}</div>
                            <div class="acc-kpi__hint">{{ isDashboardFilterAll ? "Sum across all semesters" : "Capacity minus occupancy" }}</div>
                        </div>
                        <div class="acc-kpi">
                            <div class="acc-kpi__label">Current occupancy</div>
                            <div class="acc-kpi__value">{{ formatNumber(summary.current_occupancy) }}</div>
                            <div class="acc-kpi__hint">{{ isDashboardFilterAll ? "Sum across all semesters" : "Assigned students" }}</div>
                        </div>
                        <div class="acc-kpi">
                            <div class="acc-kpi__label">Occupied villas</div>
                            <div class="acc-kpi__value">
                                {{ formatNumber(summary.occupied_villas) }}
                                <span v-if="summary.villa_count" class="text-base text-gray-400">/ {{ formatNumber(summary.villa_count) }}</span>
                            </div>
                            <div class="acc-kpi__hint">{{ isDashboardFilterAll ? "Sum across all semesters" : "Villas with at least one student" }}</div>
                        </div>
                        <div class="acc-kpi acc-kpi--info">
                            <div class="acc-kpi__label">Room / bed layout</div>
                            <div class="acc-kpi__value acc-kpi__value--sm">{{ summary.room_bed_layout || "—" }}</div>
                            <div class="acc-kpi__hint">{{ availableRoomsLabel }}</div>
                        </div>
                    </div>

                    <div class="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
                        <section class="bg-white rounded-2xl border shadow-lg p-6">
                            <h2 class="text-lg font-semibold text-gray-800 mb-1">Occupancy</h2>
                            <p class="text-xs text-gray-500 mb-4">{{ dashboardSemesterLabel }}</p>
                            <apexchart
                                v-if="occupancyChartHasData"
                                type="donut"
                                height="280"
                                :options="occupancyDonutOptions"
                                :series="occupancyDonutSeries"
                            />
                            <div v-else class="acc-empty">
                                <font-awesome-icon :icon="['fas', 'chart-pie']" class="acc-empty__icon" />
                                <p class="acc-empty__title">No occupancy data</p>
                                <p class="acc-empty__text">Configure accommodation and add assignments to see occupancy for this semester.</p>
                            </div>
                        </section>
                        <section class="bg-white rounded-2xl border shadow-lg p-6">
                            <h2 class="text-lg font-semibold text-gray-800 mb-1">Student distribution by sponsor</h2>
                            <p class="text-xs text-gray-500 mb-4">Calculated from assigned students</p>
                            <apexchart
                                v-if="sponsorChartHasData"
                                type="bar"
                                height="280"
                                :options="sponsorBarOptions"
                                :series="sponsorBarSeries"
                            />
                            <div v-else class="acc-empty">
                                <font-awesome-icon :icon="['fas', 'users']" class="acc-empty__icon" />
                                <p class="acc-empty__title">No sponsor data</p>
                                <p class="acc-empty__text">Assigned students with a sponsor will appear here.</p>
                            </div>
                        </section>
                    </div>

                    <section v-if="!isDashboardFilterAll" class="bg-white rounded-2xl border shadow-lg p-6 mb-6">
                        <h2 class="text-lg font-semibold text-gray-800 mb-1">Students by villa</h2>
                        <p class="text-xs text-gray-500 mb-4">{{ dashboardSemesterLabel }} · click a villa to open assignments</p>
                        <apexchart
                            v-if="villaChartHasData"
                            class="acc-villa-chart"
                            type="bar"
                            height="320"
                            :options="villaBarOptions"
                            :series="villaBarSeries"
                            @dataPointSelection="onVillaBarSelect"
                        />
                        <div v-else class="acc-empty">
                            <font-awesome-icon :icon="['fas', 'home']" class="acc-empty__icon" />
                            <p class="acc-empty__title">No villa occupancy</p>
                            <p class="acc-empty__text">Assign students to villas for this semester to see allocation by villa.</p>
                        </div>
                    </section>

                    <section class="bg-white rounded-2xl border shadow-lg p-6 md:p-8 mb-6">
                        <div class="flex flex-col gap-4 mb-6">
                            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                <h2 class="text-lg font-semibold text-gray-800 flex items-center">
                                    <font-awesome-icon :icon="['fas', 'cog']" class="text-orange-600 mr-2" />
                                    Manage Sections
                                </h2>
                            </div>
                            <div class="acc-semester-bar acc-semester-bar--manage">
                                <div class="acc-semester-bar__label">
                                    <font-awesome-icon :icon="['fas', 'calendar-alt']" class="mr-2" />
                                    <span>Semester</span>
                                </div>
                                <div class="acc-semester-bar__pills">
                                    <button
                                        v-for="opt in semesterFilterOptions"
                                        :key="'manage-sem-' + opt.value + opt.label"
                                        type="button"
                                        class="acc-semester-pill"
                                        :class="{ 'acc-semester-pill--active': dashboardSemester === opt.value }"
                                        @click="setDashboardSemester(opt.value)"
                                    >
                                        {{ opt.label }}
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                            <button
                                v-for="module in modules"
                                :key="module.key"
                                type="button"
                                class="acc-module"
                                @click="openModule(module)"
                            >
                                <div class="acc-module__icon">{{ module.icon }}</div>
                                <div class="acc-module__label">{{ module.label }}</div>
                            </button>
                        </div>
                    </section>
                </template>
            </div>
        </div>

        <transition name="fade" mode="out-in">
            <base-full-modal v-if="activeModule" :title="activeModule.label" @close="closeModule">
                <component
                    :is="activeModuleComponent"
                    :key="activeModuleKey"
                    :entity="entity"
                    :campus="campusCode"
                    :semester="activeSemesterForManage"
                    :filter-all="isDashboardFilterAll"
                    :prefill-semester="configPrefillSemester"
                    :initial-villa="assignmentPrefillVilla"
                    @changed="onModuleChanged"
                    @open-configuration="openConfigurationFromAssignments"
                    @open-assignments="openAssignmentsFromConfiguration"
                />
            </base-full-modal>
        </transition>
    </div>
</template>

<script>
import { defineAsyncComponent } from "vue";
import gql from "graphql-tag";
import * as XLSX from "xlsx";
import BreadCrumb from "../../../components/shared/BreadCrumb.vue";
import {
    ADPOLY_ENTITY,
    getCampusConfig,
    isValidCampus,
    buildAccommodationSemesterLists,
    ACCOMMODATION_SEMESTER_FILTER_ALL,
} from "../config";
import { buildAccommodationExportSheets } from "./accommodationExport.js";

var moduleComponentCache = {};

function resolveModuleComponent(key) {
    if (!key) return null;
    if (!moduleComponentCache[key]) {
        if (key === "settings") {
            moduleComponentCache[key] = defineAsyncComponent(function () {
                return import("./modules/SettingsModule.vue");
            });
        } else if (key === "assignments") {
            moduleComponentCache[key] = defineAsyncComponent(function () {
                return import("./modules/AssignmentsModule.vue");
            });
        }
    }
    return moduleComponentCache[key] || null;
}

const OVERVIEW_QUERY = gql`
    query AccommodationOverview($entity: String!, $campus: String!, $semester: String) {
        accommodationOverview(entity: $entity, campus: $campus, semester: $semester) {
            error
            message
            data {
                semesters
                settings {
                    total_capacity
                    villa_count
                    room_bed_layout
                    available_rooms_beds
                }
                summary {
                    total_capacity
                    available_beds
                    current_occupancy
                    occupied_villas
                    villa_count
                    available_rooms_beds_label
                    room_bed_layout
                    sponsor_breakdown {
                        label
                        value
                    }
                    villa_breakdown {
                        villa_no
                        label
                        students
                        rooms
                        beds_total
                        beds_occupied
                        beds_available
                    }
                }
            }
        }
    }
`;

const SEMESTERS_QUERY = gql`
    query AccommodationDashboardSemesters($entity: String!, $campus: String!) {
        accommodationSemesters(entity: $entity, campus: $campus) {
            error
            message
            data
        }
    }
`;

export default {
    name: "adpoly-accommodation",
    components: { BreadCrumb },
    props: ["campus"],
    data() {
        return {
            pageLoading: true,
            isExportExcel: false,
            dashboardSemester: null,
            semesterInitialized: false,
            semesterFilterOptions: [],
            formSemesterOptions: [],
            summary: {
                total_capacity: 0,
                available_beds: 0,
                current_occupancy: 0,
                occupied_villas: 0,
                villa_count: 0,
                available_rooms_beds_label: "",
                room_bed_layout: "",
                sponsor_breakdown: [],
                villa_breakdown: [],
            },
            activeModule: null,
            configPrefillSemester: "",
            assignmentPrefillVilla: "",
            modules: [
                {
                    key: "settings",
                    label: "Configuration",
                    icon: "⚙️",
                },
                {
                    key: "assignments",
                    label: "Assignments",
                    icon: "🛏️",
                },
            ],
        };
    },
    computed: {
        entity() {
            return ADPOLY_ENTITY;
        },
        campusConfig() {
            return getCampusConfig(this.campus);
        },
        campusCode() {
            return this.campusConfig ? this.campusConfig.campusCode : "";
        },
        campusTitle() {
            return this.campusConfig ? this.campusConfig.title : "";
        },
        dashboardSemesterLabel() {
            if (this.dashboardSemester === ACCOMMODATION_SEMESTER_FILTER_ALL) return "All semesters";
            return this.dashboardSemester || "—";
        },
        isDashboardFilterAll() {
            return this.dashboardSemester === ACCOMMODATION_SEMESTER_FILTER_ALL || this.dashboardSemester === null;
        },
        activeSemesterForManage() {
            if (this.isDashboardFilterAll) return "";
            return this.dashboardSemester || "";
        },
        availableRoomsLabel() {
            if (this.summary.available_rooms_beds_label) {
                return this.summary.available_rooms_beds_label;
            }
            return "Configure in Settings";
        },
        occupancyDonutSeries() {
            var occupied = this.summary.current_occupancy || 0;
            var available = this.summary.available_beds || 0;
            return [occupied, available];
        },
        occupancyChartHasData() {
            var occupied = this.summary.current_occupancy || 0;
            var available = this.summary.available_beds || 0;
            return occupied > 0 || available > 0;
        },
        occupancyDonutOptions() {
            return {
                chart: { fontFamily: "inherit" },
                labels: ["Occupied", "Available"],
                colors: ["#ea580c", "#e2e8f0"],
                legend: { position: "bottom" },
                dataLabels: { enabled: true },
            };
        },
        sponsorChartHasData() {
            return this.summary.sponsor_breakdown && this.summary.sponsor_breakdown.length > 0;
        },
        sponsorBarSeries() {
            var values = [];
            if (this.summary.sponsor_breakdown) {
                for (var i = 0; i < this.summary.sponsor_breakdown.length; i++) {
                    values.push(this.summary.sponsor_breakdown[i].value);
                }
            }
            return [{ name: "Students", data: values }];
        },
        sponsorBarOptions() {
            var categories = [];
            if (this.summary.sponsor_breakdown) {
                for (var i = 0; i < this.summary.sponsor_breakdown.length; i++) {
                    categories.push(this.summary.sponsor_breakdown[i].label);
                }
            }
            var asCount = function (val) {
                var n = Number(val);
                if (isNaN(n)) return "0";
                return String(Math.round(n));
            };
            return {
                chart: { fontFamily: "inherit", toolbar: { show: false } },
                plotOptions: { bar: { borderRadius: 4, borderRadiusApplication: "end", horizontal: true } },
                xaxis: {
                    categories: categories,
                    decimalsInFloat: 0,
                    labels: {
                        formatter: asCount,
                    },
                },
                colors: ["#c2410c"],
                dataLabels: {
                    enabled: true,
                    formatter: asCount,
                },
                tooltip: {
                    y: {
                        formatter: asCount,
                    },
                },
            };
        },
        villaChartHasData() {
            if (this.isDashboardFilterAll) return false;
            var rows = this.summary.villa_breakdown || [];
            for (var i = 0; i < rows.length; i++) {
                if ((rows[i].students || 0) > 0) return true;
            }
            return false;
        },
        villaBarSeries() {
            var values = [];
            var rows = this.summary.villa_breakdown || [];
            for (var i = 0; i < rows.length; i++) {
                values.push(rows[i].students);
            }
            return [{ name: "Students", data: values }];
        },
        villaBarOptions() {
            var rows = this.summary.villa_breakdown || [];
            var categories = [];
            for (var i = 0; i < rows.length; i++) {
                categories.push(rows[i].label);
            }
            var asCount = function (val) {
                var n = Number(val);
                if (isNaN(n)) return "0";
                return String(Math.round(n));
            };
            return {
                chart: { fontFamily: "inherit", toolbar: { show: false } },
                plotOptions: {
                    bar: {
                        borderRadius: 4,
                        borderRadiusApplication: "end",
                        columnWidth: "55%",
                    },
                },
                xaxis: {
                    categories: categories,
                },
                yaxis: {
                    decimalsInFloat: 0,
                    labels: {
                        formatter: asCount,
                    },
                    title: {
                        text: "Students allocated",
                    },
                },
                colors: ["#ea580c"],
                dataLabels: {
                    enabled: true,
                    formatter: asCount,
                },
                tooltip: {
                    custom: function (opts) {
                        var item = rows[opts.dataPointIndex];
                        if (!item) return "";
                        return (
                            '<div style="background:#fff;border:1px solid #e2e8f0;border-radius:0.75rem;padding:0.75rem 0.9rem;min-width:11rem;box-shadow:0 10px 24px -12px rgba(15,23,42,0.35);">' +
                            '<div style="font-size:0.8125rem;font-weight:700;color:#0f172a;margin-bottom:0.5rem;">' +
                            item.label +
                            "</div>" +
                            '<div style="display:flex;justify-content:space-between;gap:1rem;font-size:0.75rem;color:#64748b;margin-bottom:0.25rem;"><span>Rooms</span><strong style="color:#0f172a;">' +
                            item.rooms +
                            "</strong></div>" +
                            '<div style="display:flex;justify-content:space-between;gap:1rem;font-size:0.75rem;color:#64748b;margin-bottom:0.25rem;"><span>Beds occupied</span><strong style="color:#0f172a;">' +
                            item.beds_occupied +
                            "</strong></div>" +
                            '<div style="display:flex;justify-content:space-between;gap:1rem;font-size:0.75rem;color:#64748b;margin-bottom:0.35rem;"><span>Beds available</span><strong style="color:#0f172a;">' +
                            item.beds_available +
                            "</strong></div>" +
                            '<div style="font-size:0.6875rem;color:#94a3b8;border-top:1px solid #f1f5f9;padding-top:0.35rem;">' +
                            item.students +
                            " student" +
                            (item.students === 1 ? "" : "s") +
                            " allocated · " +
                            item.beds_total +
                            " beds total</div>" +
                            '<div style="font-size:0.6875rem;color:#c2410c;margin-top:0.35rem;">Click to view assignments</div>' +
                            "</div>"
                        );
                    },
                },
            };
        },
        breadCrumbItems() {
            var campusKey = this.campusConfig ? this.campusConfig.key : "";
            return [
                { label: "Dashboard", path: "/dashboard" },
                { label: "ADPOLY", path: "/adpoly" },
                { label: this.campusTitle || "Campus", path: campusKey ? "/adpoly/" + campusKey : "/adpoly" },
                { label: "Campus", path: campusKey ? "/adpoly/" + campusKey + "/campus" : "/adpoly" },
                { label: "Accommodation" },
            ];
        },
        activeModuleComponent() {
            if (!this.activeModule) return null;
            return resolveModuleComponent(this.activeModule.key);
        },
        activeModuleKey() {
            if (!this.activeModule) return "none";
            return (
                this.activeModule.key +
                "-" +
                (this.isDashboardFilterAll ? "all" : this.activeSemesterForManage) +
                "-" +
                (this.configPrefillSemester || "") +
                "-" +
                (this.assignmentPrefillVilla || "")
            );
        },
    },
    created() {
        this.validateCampus();
        this.initDashboard();
    },
    watch: {
        campus: function () {
            this.validateCampus();
            this.semesterInitialized = false;
            this.dashboardSemester = null;
            this.initDashboard();
        },
    },
    methods: {
        validateCampus() {
            if (!isValidCampus(this.campus)) {
                this.$router.push({ path: "/notFound" });
            }
        },
        setDashboardSemester(value) {
            this.dashboardSemester = value;
            this.loadOverview();
        },
        initDashboard() {
            if (!this.campusCode) return;
            if (this.semesterInitialized) {
                this.loadOverview();
                return;
            }
            this.pageLoading = true;
            var self = this;
            this.$apollo
                .query({
                    query: SEMESTERS_QUERY,
                    variables: {
                        entity: this.entity,
                        campus: this.campusCode,
                    },
                    fetchPolicy: "network-only",
                })
                .then(function (resp) {
                    var root = resp.data && resp.data.accommodationSemesters;
                    if (root && root.error) throw new Error(root.message);
                    var lists = buildAccommodationSemesterLists(root && root.data ? root.data : []);
                    self.semesterFilterOptions = lists.filterSemesters;
                    self.formSemesterOptions = lists.formSemesters;
                    self.dashboardSemester = lists.latestSemester || ACCOMMODATION_SEMESTER_FILTER_ALL;
                    self.semesterInitialized = true;
                    self.loadOverview();
                })
                .catch(function (err) {
                    self.pageLoading = false;
                    if (self.$swal) {
                        self.$swal.fire({
                            icon: "error",
                            title: "Error",
                            text: err.message || "Failed to load semesters.",
                        });
                    }
                });
        },
        loadOverview() {
            if (!this.campusCode) return;
            this.pageLoading = true;
            var semesterArg =
                this.dashboardSemester === ACCOMMODATION_SEMESTER_FILTER_ALL ? "" : this.dashboardSemester;
            this.$apollo
                .query({
                    query: OVERVIEW_QUERY,
                    variables: {
                        entity: this.entity,
                        campus: this.campusCode,
                        semester: semesterArg || null,
                    },
                    fetchPolicy: "network-only",
                })
                .then((resp) => {
                    var root = resp.data && resp.data.accommodationOverview;
                    if (root && root.error) throw new Error(root.message);
                    var data = root && root.data ? root.data : null;
                    if (data && data.summary) {
                        this.summary = Object.assign(
                            {
                                total_capacity: 0,
                                available_beds: 0,
                                current_occupancy: 0,
                                occupied_villas: 0,
                                villa_count: 0,
                                available_rooms_beds_label: "",
                                room_bed_layout: "",
                                sponsor_breakdown: [],
                                villa_breakdown: [],
                            },
                            data.summary,
                            {
                                sponsor_breakdown: data.summary.sponsor_breakdown || [],
                                villa_breakdown: data.summary.villa_breakdown || [],
                            }
                        );
                    }
                    var lists = buildAccommodationSemesterLists(data && data.semesters ? data.semesters : []);
                    this.semesterFilterOptions = lists.filterSemesters;
                    this.formSemesterOptions = lists.formSemesters;
                    if (!this.semesterInitialized) {
                        this.dashboardSemester = lists.latestSemester || ACCOMMODATION_SEMESTER_FILTER_ALL;
                        this.semesterInitialized = true;
                    }
                })
                .catch((err) => {
                    if (this.$swal) {
                        this.$swal.fire({ icon: "error", title: "Error", text: err.message || "Failed to load overview." });
                    }
                })
                .finally(() => {
                    this.pageLoading = false;
                });
        },
        formatNumber(value) {
            if (value === null || value === undefined || value === "") return "—";
            return Number(value).toLocaleString();
        },
        findModule(key) {
            for (var i = 0; i < this.modules.length; i++) {
                if (this.modules[i].key === key) return this.modules[i];
            }
            return null;
        },
        onVillaBarSelect(event, chartContext, config) {
            var index =
                config && config.dataPointIndex !== undefined && config.dataPointIndex !== null
                    ? config.dataPointIndex
                    : -1;
            if (index < 0) return;
            if (this._villaBarClickLock) return;
            this._villaBarClickLock = true;
            var self = this;
            setTimeout(function () {
                self._villaBarClickLock = false;
            }, 400);
            var rows = this.summary.villa_breakdown || [];
            var item = rows[index];
            if (!item || item.villa_no == null) return;
            this.openAssignmentsFromVilla(item.villa_no);
        },
        openAssignmentsFromVilla(villaNo) {
            var assignmentsModule = this.findModule("assignments");
            if (!assignmentsModule) return;
            this.configPrefillSemester = "";
            this.assignmentPrefillVilla = String(villaNo);
            this.activeModule = assignmentsModule;
        },
        openModule(module) {
            this.configPrefillSemester = "";
            this.assignmentPrefillVilla = "";
            this.activeModule = module;
        },
        closeModule() {
            this.activeModule = null;
            this.configPrefillSemester = "";
            this.assignmentPrefillVilla = "";
        },
        openConfigurationFromAssignments(semester) {
            this.configPrefillSemester = semester || "";
            this.assignmentPrefillVilla = "";
            this.activeModule = this.findModule("settings");
        },
        openAssignmentsFromConfiguration(semester) {
            var sem = semester || "";
            if (!sem) return;
            this.configPrefillSemester = "";
            this.assignmentPrefillVilla = "";
            this.dashboardSemester = sem;
            this.semesterInitialized = true;
            this.activeModule = this.findModule("assignments");
            this.loadOverview();
        },
        onModuleChanged(payload) {
            if (payload && payload.configurationDeleted) {
                this.dashboardSemester = ACCOMMODATION_SEMESTER_FILTER_ALL;
            }
            this.loadOverview();
        },
        exportAllExcel() {
            var semester = this.activeSemesterForManage || "";
            this.isExportExcel = true;
            var self = this;
            buildAccommodationExportSheets(this.$apollo, this.entity, this.campusCode, semester)
                .then(function (sheets) {
                    var wb = XLSX.utils.book_new();
                    for (var i = 0; i < sheets.length; i++) {
                        var sheet = sheets[i];
                        var ws = XLSX.utils.json_to_sheet(sheet.rows);
                        XLSX.utils.book_append_sheet(wb, ws, sheet.name);
                    }
                    var semesterLabel = semester || "ALL";
                    XLSX.writeFile(wb, "Accommodation-" + self.campusCode + "-" + semesterLabel + ".xlsx");
                })
                .catch(function (err) {
                    if (self.$swal) {
                        self.$swal.fire({ icon: "error", title: "Export failed", text: err.message || "Could not export." });
                    }
                })
                .finally(function () {
                    self.isExportExcel = false;
                });
        },
    },
};
</script>

<style lang="scss" scoped>
.acc-hero {
    background: linear-gradient(135deg, #fff7ed 0%, #ffffff 55%);
    border-color: #fed7aa;
}
.acc-hero__eyebrow {
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #c2410c;
}
.acc-export-btn {
    background: #fff;
    border: 1px solid #fdba74;
    color: #9a3412;
}
.acc-semester-bar {
    margin-top: 1.5rem;
    padding-top: 1rem;
    border-top: 1px solid #ffedd5;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}
.acc-semester-bar--manage {
    margin-top: 0;
    padding-top: 0.85rem;
    border-top: 1px solid #e2e8f0;
}
@media (min-width: 768px) {
    .acc-semester-bar {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
    }
}
.acc-semester-bar__label {
    font-size: 0.875rem;
    font-weight: 600;
    color: #475569;
    display: flex;
    align-items: center;
}
.acc-semester-bar__pills {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
}
.acc-semester-pill {
    padding: 0.35rem 0.85rem;
    border-radius: 9999px;
    font-size: 0.8125rem;
    font-weight: 600;
    border: 1px solid #fdba74;
    background: #fff;
    color: #9a3412;
}
.acc-semester-pill--active {
    background: #ea580c;
    border-color: #ea580c;
    color: #fff;
}
.acc-kpi {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 1rem;
    padding: 1rem 1.1rem;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}
.acc-kpi--info .acc-kpi__value--sm {
    font-size: 1.1rem;
}
.acc-kpi__label {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    color: #64748b;
}
.acc-kpi__value {
    font-size: 1.75rem;
    font-weight: 800;
    color: #0f172a;
    margin-top: 0.25rem;
}
.acc-kpi__hint {
    font-size: 0.75rem;
    color: #94a3b8;
    margin-top: 0.35rem;
    line-height: 1.35;
}
.acc-empty {
    min-height: 16rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 1.5rem 1rem;
}
.acc-empty__icon {
    font-size: 2rem;
    color: #fdba74;
    margin-bottom: 0.75rem;
}
.acc-empty__title {
    font-size: 0.95rem;
    font-weight: 600;
    color: #334155;
    margin-bottom: 0.35rem;
}
.acc-empty__text {
    font-size: 0.8125rem;
    color: #94a3b8;
    max-width: 22rem;
    line-height: 1.45;
}
.acc-module {
    border: 1px solid #e2e8f0;
    border-radius: 0.85rem;
    padding: 1rem;
    text-align: center;
    background: #fff;
    transition: box-shadow 0.15s ease;
}
.acc-module:hover {
    box-shadow: 0 8px 20px -12px rgba(234, 88, 12, 0.45);
}
.acc-module__icon {
    font-size: 1.75rem;
    margin-bottom: 0.5rem;
}
.acc-module__label {
    font-size: 0.875rem;
    font-weight: 600;
    color: #334155;
}
.acc-villa-chart :deep(.apexcharts-bar-area),
.acc-villa-chart :deep(.apexcharts-series path),
.acc-villa-chart :deep(.apexcharts-xaxis-label) {
    cursor: pointer;
}
</style>
